import { useState, useMemo } from 'react';
import { suggestRegex } from '@capture/shared';
import type { SuggestResult } from '@capture/shared';

export interface RegexAssistPanelProps {
  pattern: string;
  onAccept: (pattern: string) => void;
}

export function RegexAssistPanel({ pattern, onAccept }: RegexAssistPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [validText, setValidText] = useState('');
  const [invalidText, setInvalidText] = useState('');
  const [suggestion, setSuggestion] = useState<SuggestResult | null>(null);

  function handleSuggest() {
    const valid = validText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const invalid = invalidText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const result = suggestRegex({ valid, invalid });
    setSuggestion(result);
  }

  // The pattern to test against: use suggestion if available, else the prop
  const testPattern = suggestion?.pattern || pattern;

  // Collect all samples for the test grid
  const testResults = useMemo(() => {
    if (!testPattern) return [];
    let re: RegExp;
    try {
      re = new RegExp(testPattern);
    } catch {
      return [];
    }

    const validSamples = validText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const invalidSamples = invalidText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    return [
      ...validSamples.map((s) => ({ value: s, expected: true, pass: re.test(s) })),
      ...invalidSamples.map((s) => ({ value: s, expected: false, pass: !re.test(s) })),
    ];
  }, [testPattern, validText, invalidText]);

  return (
    <div className="regex-assist" data-testid="regex-assist-panel">
      <div
        className="regex-assist__header"
        onClick={() => setExpanded((e) => !e)}
        data-testid="regex-assist-toggle"
      >
        {expanded ? '\u25BE' : '\u25B8'} Regex Assist
      </div>

      {expanded && (
        <div className="regex-assist__body">
          <label className="regex-assist__label">Valid examples (one per line)</label>
          <textarea
            className="regex-assist__textarea"
            value={validText}
            onChange={(e) => setValidText(e.target.value)}
            placeholder={'INV-001234\nINV-999999'}
            data-testid="regex-valid-input"
          />

          <label className="regex-assist__label">Invalid examples (one per line)</label>
          <textarea
            className="regex-assist__textarea"
            value={invalidText}
            onChange={(e) => setInvalidText(e.target.value)}
            placeholder={'PO-1234\nINV1234'}
            data-testid="regex-invalid-input"
          />

          <button
            className="regex-assist__suggest-btn"
            onClick={handleSuggest}
            data-testid="regex-suggest-btn"
          >
            Suggest
          </button>

          {suggestion && suggestion.pattern && (
            <div className="regex-assist__result" data-testid="regex-suggestion">
              <div className="regex-assist__pattern">
                Suggested: <code>{suggestion.pattern}</code>
              </div>
              {suggestion.description && (
                <div className="regex-assist__description">{suggestion.description}</div>
              )}
            </div>
          )}

          {testResults.length > 0 && (
            <div className="regex-assist__tests">
              <div className="regex-assist__label">Test Results:</div>
              <ul className="regex-assist__test-grid" data-testid="regex-test-grid">
                {testResults.map((r, i) => (
                  <li
                    key={i}
                    className={r.pass ? 'regex-assist__test-pass' : 'regex-assist__test-fail'}
                  >
                    {r.pass ? (r.expected ? '\u2713' : '\u2717') : r.expected ? '\u2717' : '\u2713'}{' '}
                    {r.value}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {suggestion && suggestion.pattern && (
            <button
              className="regex-assist__accept-btn"
              onClick={() => onAccept(suggestion.pattern)}
              data-testid="regex-accept-btn"
            >
              Accept
            </button>
          )}
        </div>
      )}
    </div>
  );
}
